import { useState } from 'react';
import { subtitleScene, subtitleVariants } from '@/mocks/subtitleSample';

type VariantId = (typeof subtitleVariants)[number]['id'];

export default function SubtitlePreview() {
  const [variant, setVariant] = useState<VariantId>('cn');
  const activeVariant = subtitleVariants.find((item) => item.id === variant) ?? subtitleVariants[0];

  return (
    <section id="preview" className="w-full scroll-mt-28 bg-background-100 py-14 md:py-20">
      <div className="mx-auto w-full max-w-[1240px] px-4 md:px-6">
        <div className="max-w-2xl">
          <span className="font-label text-xs font-medium uppercase tracking-widest text-primary-600">
            Online Preview
          </span>
          <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            字幕在线试读：同一段对白，三种译法
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
            下面用一个样例场景演示「中文版 / 国语版 / 中文字幕」在同一段对白上的不同处理方式。
            切换版本即可对比译文风格，帮助你在正式观看前先选定方向。
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-4">
          <div className="lg:col-span-1">
            <div className="flex flex-row gap-1 rounded-full border border-background-200 bg-background-50 p-1 lg:flex-col lg:rounded-lg lg:p-2">
              {subtitleVariants.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setVariant(item.id)}
                  aria-pressed={variant === item.id}
                  className={`flex flex-1 cursor-pointer flex-col items-start rounded-full px-4 py-2 text-left transition-colors lg:flex-none lg:rounded-md lg:px-4 lg:py-3 ${
                    variant === item.id
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
                  }`}
                >
                  <span className="font-label text-sm font-medium">{item.label}</span>
                  <span
                    className={`mt-0.5 hidden text-xs lg:block ${
                      variant === item.id ? 'text-background-100/80' : 'text-foreground-500'
                    }`}
                  >
                    {item.desc}
                  </span>
                </button>
              ))}
            </div>

            <div className="mt-4 hidden rounded-lg border border-background-200 bg-background-50 p-4 lg:block">
              <p className="font-label text-xs font-medium text-foreground-900">{subtitleScene.title}</p>
              <p className="mt-2 text-xs leading-relaxed text-foreground-500">{subtitleScene.note}</p>
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-background-200 bg-background-100/70 px-5 py-3">
                <span className="flex items-center gap-2 font-label text-sm font-medium text-foreground-900">
                  <i className="ri-play-circle-line text-base text-primary-600" aria-hidden="true"></i>
                  {subtitleScene.title}
                </span>
                <span className="rounded-full bg-accent-100 px-3 py-1 font-label text-xs text-accent-900">
                  当前版本：{activeVariant.label}
                </span>
              </div>

              <ul className="divide-y divide-background-200">
                {subtitleScene.lines.map((line) => (
                  <li key={line.time} className="flex gap-4 px-5 py-4">
                    <span className="mt-0.5 shrink-0 font-mono text-xs tabular-nums text-foreground-400">
                      {line.time}
                    </span>
                    <div className="min-w-0">
                      <span className="rounded bg-secondary-100 px-2 py-0.5 font-label text-xs text-secondary-900">
                        {line.speaker}
                      </span>
                      <p className="mt-2 text-sm leading-relaxed text-foreground-800">
                        {line[variant]}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <p className="mt-4 flex items-start gap-2 text-xs leading-relaxed text-foreground-500 lg:hidden">
              <i className="ri-information-line mt-0.5 text-sm" aria-hidden="true"></i>
              {subtitleScene.note}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}